import {
  blue,
  gold,
  green,
  grey,
  magenta,
  orange,
  Palette,
  purple,
  red,
} from '@ant-design/colors'
import { useCanAccessPath } from '@hooks/useCanAccessPath'
import { withPreviewTag } from '@components/Feature'
import { useFeature } from '@utils/featureFlags'
import { Link } from 'react-router-dom'

export interface CustomMenuItemChildren {
  key: string
  label: React.ReactNode | string
  title: string
  iconPage: string
  description: string
  url: string
  disabled: boolean
  color?: Palette
}

export interface CustomMenuItem {
  key: string
  label: React.ReactNode | string
  color?: Palette
  disabled?: boolean
  children?: CustomMenuItemChildren[]
}

/**
 * A group or entry as declared below. Neither carries a `disabled` flag: the
 * gate is resolved from ROUTE_PERMISSIONS out of the entry's own `url`, so a
 * menu entry can never demand a different permission from the page it opens.
 */
type MenuChildDeclaration = Omit<CustomMenuItemChildren, 'disabled'>

type MenuGroupDeclaration = Omit<CustomMenuItem, 'disabled' | 'children'> & {
  children?: MenuChildDeclaration[]
}

export interface CustomCategoryMenu {
  key: 'tools' | 'settings'
  label?: string
  items: CustomMenuItem[]
}

export const useSettingMenuItems = (): {
  menu: CustomCategoryMenu[]
  menuItems: CustomMenuItem[]
  flatChildren: CustomMenuItemChildren[]
} => {
  const { canAccessPath } = useCanAccessPath()
  const { enabled: userDataPrivacy, preview: userDataPrivacyPreview } =
    useFeature('userDataPrivacy')
  const { enabled: webBooking, preview: webBookingPreview } =
    useFeature('webBooking')

  /**
   * An entry is open when the admin may open the page it links to; a group is
   * open when at least one of its entries is — which is why the Business group
   * used to be disabled for exactly the role its entries belong to.
   */
  const resolveGates = (groups: MenuGroupDeclaration[]): CustomMenuItem[] =>
    groups.map((group) => {
      const children = group.children?.map((child) => ({
        ...child,
        disabled: !canAccessPath(`/settings/${child.url}`),
      }))

      return {
        ...group,
        children,
        disabled: (children ?? []).every((child) => child.disabled),
      }
    })

  const toolsMenu: CustomMenuItem[] = resolveGates([
    {
      key: 'tools',
      label: 'Tools',
      color: blue,
      children: [
        {
          key: 'availability-viewer',
          label: (
            <Link to={'booking/availability-viewer'}>Availability viewer</Link>
          ),
          title: 'Availability viewer',
          iconPage: 'ClockCircleOutlined',
          description: 'Check availability by time and tier.',
          url: 'booking/availability-viewer',
        },
      ],
    },
  ])

  const settingsMenu: CustomMenuItem[] = resolveGates([
    {
      key: 'administrators',
      label: 'Administrators',
      color: purple,
      children: [
        {
          key: 'admins',
          label: <Link to={'admins'}>Administrators list</Link>,
          title: 'Administrators list',
          iconPage: 'ProductOutlined',
          description: 'View and manage all current dashboard administrators.',
          url: 'admins',
        },
        {
          key: 'invites',
          label: <Link to={'admins/invites'}>Pending invitations</Link>,
          title: 'Pending invitations',
          iconPage: 'ProfileOutlined',
          description:
            'Track admin invites awaiting activation and resend if needed.',
          url: 'admins/invites',
        },
        {
          key: 'roles',
          label: <Link to={'admins/roles'}>Roles & permissions</Link>,
          title: 'Roles & permissions',
          iconPage: 'ControlOutlined',
          description:
            'List permissions used in the back office for each roles',
          url: 'admins/roles',
        },
      ],
    },
    ...(userDataPrivacy
      ? [
          {
            key: 'user',
            label: withPreviewTag('User', userDataPrivacyPreview),
            color: grey,
            children: [
              {
                key: 'data-privacy',
                label: <Link to={'user/data-privacy'}>Data privacy</Link>,
                title: 'Data privacy',
                iconPage: 'UserOutlined',
                description:
                  'Set up notifications for user accounts auto-purge.',
                url: 'user/data-privacy',
              },
            ],
          },
        ]
      : []),
    {
      key: 'integrations',
      label: 'Integrations',
      color: orange,
      children: [
        {
          key: 'sevenrooms',
          label: <Link to={'integrations/sevenrooms'}>Sevenrooms</Link>,
          title: 'Sevenrooms',
          iconPage: 'ToolOutlined',
          description:
            'Connect your Sevenrooms account to sync reservations and profiles.',
          url: 'integrations/sevenrooms',
        },
        {
          key: 'trivec',
          label: <Link to={'integrations/trivec'}>Trivec</Link>,
          title: 'Trivec',
          iconPage: 'ToolOutlined',
          description:
            'Link Trivec POS to retrieve order and reservation data.',
          url: 'integrations/trivec',
        },
        {
          key: 'adyen',
          label: <Link to={'integrations/adyen'}>Adyen</Link>,
          title: 'Adyen',
          iconPage: 'ToolOutlined',
          description: 'Connect your Adyen account to enable payment.',
          url: 'integrations/adyen',
        },
        {
          key: 'uber',
          label: <Link to={'integrations/uber'}>Uber</Link>,
          title: 'Uber',
          iconPage: 'ToolOutlined',
          description: 'Enable the Uber VTC button on the venue page.',
          url: 'integrations/uber',
        },
        ...(webBooking
          ? [
              {
                key: 'web-booking',
                label: withPreviewTag(
                  <Link to={'integrations/web-booking'}>Web booking</Link>,
                  webBookingPreview
                ),
                title: 'Web booking',
                iconPage: 'GlobalOutlined',
                description:
                  'Open or close the booking website and its widget.',
                url: 'integrations/web-booking',
              },
            ]
          : []),
      ],
    },
    {
      key: 'booking',
      label: 'Booking',
      color: blue,
      children: [
        {
          key: 'bookings',
          label: <Link to={'booking/bookings'}>Generals</Link>,
          title: 'Generals',
          iconPage: 'BookOutlined',
          description:
            'Configure booking defaults like cut-off times and statuses.',
          url: 'booking/bookings',
        },
        {
          key: 'seating-areas',
          label: <Link to={'booking/seating-areas'}>Seating areas</Link>,
          title: 'Seating areas',
          iconPage: 'CompassOutlined',
          description: 'Manage and label areas for seating and booking.',
          url: 'booking/seating-areas',
        },
        {
          key: 'booking-notifications',
          label: (
            <Link to={'booking/booking-notifications'}>
              Booking notifications
            </Link>
          ),
          title: 'Booking notifications',
          iconPage: 'BellOutlined',
          description: 'Set transactional notifications for bookings changes.',
          url: 'booking/booking-notifications',
        },
        {
          key: 'payment-link',
          label: <Link to={'booking/payment-link'}>Payment link</Link>,
          title: 'Payment link',
          iconPage: 'LinkOutlined',
          description:
            'Manage and customize payment link notifications content.',
          url: 'booking/payment-link',
        },
      ],
    },
    {
      key: 'membership',
      label: 'Membership',
      color: magenta,
      children: [
        {
          key: 'membership-notifications',
          label: (
            <Link to={'membership/membership-notifications'}>
              Membership notifications
            </Link>
          ),
          title: 'Membership notifications',
          iconPage: 'BellOutlined',
          description:
            'Set transactional notifications for membership changes.',
          url: 'membership/membership-notifications',
        },
        {
          key: 'programs-list',
          label: <Link to={'membership/programs/list'}>Programs</Link>,
          title: 'Programs',
          iconPage: 'ContainerOutlined',
          description:
            'Manage membership programs, onboarding forms, payment settings or branding.',
          url: 'membership/programs/list',
        },
        {
          key: 'membership-booklets',
          label: <Link to={'membership/booklets'}>Membership demo tab</Link>,
          title: 'Membership demo tab',
          iconPage: 'FilePdfOutlined',
          description:
            'Set the booklet and the photo of the membership tab shown to app users who are not members yet.',
          url: 'membership/booklets',
        },
        {
          key: 'participation-statuses',
          label: (
            <Link to={'membership/participation-statuses'}>
              Participation statuses
            </Link>
          ),
          title: 'Participation statuses',
          iconPage: 'HomeOutlined',
          description: 'Define statuses for member participation tracking.',
          url: 'membership/participation-statuses',
        },
        {
          key: 'profiles-tags',
          label: (
            <Link to={'membership/profiles-tags'}>
              Membership profiles tags
            </Link>
          ),
          title: 'Membership profiles tags',
          iconPage: 'TagOutlined',
          description:
            'Tag members with custom labels to organize, group, or filter them efficiently.',
          url: 'membership/profiles-tags',
        },
        {
          key: 'subscription-tags',
          label: (
            <Link to={'membership/subscription-tags'}>Subscription tags</Link>
          ),
          title: 'Subscription tags',
          iconPage: 'TagOutlined',
          description:
            'Add tags to subscriptions to organize, group, or filter them efficiently.',
          url: 'membership/subscription-tags',
        },
        {
          key: 'complementary-items-list',
          label: (
            <Link to={'membership/complementary-items/list'}>
              Balances and credits
            </Link>
          ),
          title: 'Balances and credits',
          iconPage: 'DollarOutlined',
          description: 'Add balances and credits items to programs.',
          url: 'membership/complementary-items/list',
        },
      ],
    },
    {
      key: 'business',
      label: 'Business',
      color: gold,
      children: [
        {
          key: 'company-statuses',
          label: <Link to={'business/company-statuses'}>Company statuses</Link>,
          title: 'Company statuses',
          iconPage: 'ShopOutlined',
          description:
            'Manage internal statuses for company validation or tracking steps.',
          url: 'business/company-statuses',
        },
        {
          key: 'employee-statuses',
          label: (
            <Link to={'business/employee-statuses'}>Employee statuses</Link>
          ),
          title: 'Employee statuses',
          iconPage: 'SmileOutlined',
          description:
            'Define statuses for employees like active, pending, or paused.',
          url: 'business/employee-statuses',
        },
        {
          key: 'business-notifications',
          label: (
            <Link to={'business/business-notifications'}>
              Business notifications
            </Link>
          ),
          title: 'Business notifications',
          iconPage: 'BellOutlined',
          description:
            'Set notifications for company-related updates or business actions.',
          url: 'business/business-notifications',
        },
        {
          key: 'verifier-accounts',
          label: (
            <Link to={'business/verifier-accounts'}>Verifier accounts</Link>
          ),
          title: 'Verifier accounts',
          iconPage: 'IdcardOutlined',
          description:
            'Manage external accounts allowed to verify business employee profiles.',
          url: 'business/verifier-accounts',
        },
        {
          key: 'loyalty-tier',
          label: <Link to={'business/loyalty-tier'}>Loyalty Tier</Link>,
          title: 'Loyalty Tier',
          iconPage: 'TagOutlined',
          description:
            'Select the loyalty tier that will be assigned to employee once validated.',
          url: 'business/loyalty-tier',
        },
        // {
        //   key: 'business-venues',
        //   label: <Link to={'business/business-venues'}>Business venues</Link>,
        //   title: 'Business venues',
        //   iconPage: 'ShopOutlined',
        //   description: 'View and manage all business venues.',
        //   url: 'business/business-venues',
        //   disabled: !hasAccess(Permission.manageBusiness),
        // },
      ],
    },
    {
      key: 'platforms',
      label: 'Platforms',
      color: green,
      children: [
        {
          key: 'platforms-list',
          label: <Link to={'platforms'}>Platforms list</Link>,
          title: 'Platforms list',
          iconPage: 'UnorderedListOutlined',
          description: 'Display and manage all connected platforms.',
          url: 'platforms',
        },
        {
          key: 'mobile-apps',
          label: <Link to={'platforms/mobile-apps'}>Mobile applications</Link>,
          title: 'Mobile applications',
          iconPage: 'AndroidOutlined',
          description:
            'Adjust mobile app configuration and display across platforms.',
          url: 'platforms/mobile-apps',
        },
        {
          key: 'whatsapp',
          label: <Link to={'platforms/whatsapp'}>WhatsApp</Link>,
          title: 'WhatsApp',
          iconPage: 'MessageOutlined',
          description: 'Manage WhatsApp bot common texts, labels and settings.',
          url: 'platforms/whatsapp',
        },
      ],
    },
    {
      key: 'api',
      label: 'API',
      color: red,
      children: [
        {
          key: 'external-booking-api-keys',
          label: (
            <Link to={'api/external-booking-api-keys'}>External Booking</Link>
          ),
          title: 'External Booking',
          iconPage: 'ApiOutlined',
          description:
            'Set up external systems to book Pléthore venues through a unified booking API.',
          url: 'api/external-booking-api-keys',
        },
      ],
    },
    {
      key: 'legals',
      label: 'Legals',
      color: grey,
      children: [
        {
          key: 'legal-documents',
          label: (
            <Link to={'legals/legal-documents/list'}>Legal documents</Link>
          ),
          title: 'Legal documents',
          iconPage: 'FileTextOutlined',
          description:
            'Manage all legal documents along with their version history.',
          url: 'legals/legal-documents/list',
        },
      ],
    },
  ])
    .filter((item) => !item.disabled)
    .map((item) => ({
      ...item,
      children: item.children?.filter((children) => !children.disabled),
    }))

  const menu: CustomCategoryMenu[] = [
    {
      key: 'tools',
      label: 'Tools',
      items: toolsMenu,
    },
    {
      key: 'settings',
      items: settingsMenu,
    },
  ]

  const menuItems = menu.flatMap((category) => category.items)

  const flatChildren = menuItems
    .filter((section) => !section.disabled)
    .flatMap((section) =>
      (section.children ?? [])
        .filter((child) => !child.disabled)
        .map((child) => ({
          ...child,
          category: section.key,
          color: section.color, // on ajoute la couleur du parent ici
        }))
    )

  return { menu, menuItems, flatChildren }
}
